/* http */
import { cfg } from '../../config';
import type { ApiRsp, ReqToken, RspToken } from './types';
/*--------------------------------------------------------------------------------*/
const httpClient = async (
    url: string,
    method: string,
    body?: unknown,
    isRetry = false
): Promise<ApiRsp> => {
    const isTokenReq = url.includes(cfg.hub.OAUTH + '/token');
    const token = localStorage.getItem(cfg.cookie.TOKEN);
    const config: RequestInit = {
        method,
        headers: {
            'Content-Type': 'application/json; charset=utf-8',
            ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: body ? JSON.stringify(body) : undefined,
    };
    const resp = await fetch(url, config);
    // error 401
    if (resp.status === 401 && !isTokenReq) {
        if (isRetry) {
            window.location.replace('/logout');
            throw new Error('Unauthorized');
        }
        if (!isRefreshing) {
            isRefreshing = true;
            await getRefreshToken();
            isRefreshing = false;
            onRefreshed();
            return httpClient(url, method, body, true);
        }
        // wait new token
        return new Promise((resolve, reject) => {
            addSubscriber(() => {
                httpClient(url, method, body, true)
                    .then(resolve)
                    .catch(reject);
            });
        });
    }
    // other error
    if (!resp.ok) {
        const errMsg = await resp.json().catch(() => ({}));
        throw new Error(errMsg.message || `HTTP Error: ${resp.status}`);
    }
    return await resp.json();
};
/*--------------------------------------------------------------------------------*/
let isRefreshing = false;
let refreshSubscribers: (() => void)[] = [];
const addSubscriber = (callback: () => void) => {
    refreshSubscribers.push(callback);
};
const onRefreshed = () => {
    refreshSubscribers.forEach((cb) => cb());
    refreshSubscribers = [];
};
const getRefreshToken = async (isLine = false) => {
    if (isLine) return await lineAuth();
    try {
        const reToken = localStorage.getItem(cfg.cookie.REFRESH);
        if (!reToken) throw new Error('token not found');
        // request params
        const req: ReqToken = {
            grant_type: 'refresh_token',
            client_id: cfg.app.CODE,
            refresh_token: reToken,
        };
        // fetch
        const oauthUrl = cfg.hub.OAUTH + '/token';
        const refreshResp = await fetch(oauthUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(req)
        });
        if (!refreshResp.ok) throw new Error('refresh failed');
        const apiRsp: ApiRsp = await refreshResp.json();
        const data = apiRsp.data as RspToken;
        // save new token
        localStorage.setItem(cfg.cookie.TOKEN, data.access_token);
        localStorage.setItem(cfg.cookie.REFRESH, data.refresh_token!);
    } catch (err) {
        window.location.replace('/logout');
        throw err;
    }
};
const lineAuth = async () => {
    try {
        // request params
        const req: ReqToken = {
            grant_type: 'lineid',
            client_id: cfg.app.CODE,
            uuid: cfg.api.UUID, // line.id
        };
        // fetch
        const oauthUrl = cfg.hub.OAUTH + '/token';
        const refreshResp = await fetch(oauthUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(req)
        });
        if (!refreshResp.ok) throw new Error('refresh failed');
        const apiRsp: ApiRsp = await refreshResp.json();
        const data = apiRsp.data as RspToken;
        // save new token
        localStorage.setItem(cfg.cookie.TOKEN, data.access_token);
    } catch (err) {
        window.location.replace('/logout');
        throw err;
    }
};
/*--------------------------------------------------------------------------------*/
const buildUriQuery = (params: object): string => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
        if (val !== undefined && val !== null && val !== '') {
            query.append(key, val.toString());
        }
    });
    return query.toString();
};
/*--------------------------------------------------------------------------------*/
export const http = {
    get: (url: string) =>
        httpClient(url, 'GET'),

    post: (url: string, body: unknown) =>
        httpClient(url, 'POST', body),

    put: (url: string, body: unknown) =>
        httpClient(url, 'PUT', body),

    delete: (url: string) =>
        httpClient(url, 'DELETE'),

    getRefreshToken,
    buildUriQuery
};